<template>
  <div
    ref="reference"
    class="relative inline-block"
    @mouseenter="state.showTooltip = true"
    @mouseleave="state.showTooltip = false"
  >
    <slot />
  </div>

  <Teleport to="body">
    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-show="state.showTooltip"
        ref="floating"
        :style="{ position: strategy, top: `${y ?? 0}px`, left: `${x ?? 0}px`, zIndex: 1000 }"
        class="fixed rounded border border-white/20 p-2 text-xs shadow-lg backdrop-blur"
        :class="tooltipClasses"
        v-bind="$attrs"
      >
        <slot name="content">
          {{ text }}
        </slot>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts">
export enum TooltipStyle {
  Default = 'default',
  Dark = 'dark'
}

export enum TooltipWidth {
  Default = 'default',
  Small = 'small',
  Medium = 'medium',
  Large = 'large'
}
</script>

<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue';
import { useFloating, offset, flip, shift, autoUpdate, Placement, OffsetOptions } from '@floating-ui/vue';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    text?: string;
    style?: TooltipStyle;
    width?: TooltipWidth;
    position?: Placement;
    offsetPos?: OffsetOptions;
  }>(),
  {
    text: '',
    style: TooltipStyle.Default,
    width: TooltipWidth.Default,
    position: 'bottom',
    offsetPos: 10
  }
);

const state = reactive({ showTooltip: false });

const reference = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);

const { x, y, strategy, update } = useFloating(reference, floating, {
  placement: props.position,
  middleware: [offset(props.offsetPos), flip(), shift()],
  whileElementsMounted: autoUpdate
});

watch(
  () => state.showTooltip,
  (visible) => {
    if (visible) update();
  }
);

watch(
  () => props.position,
  () => {
    update();
  }
);

const tooltipClasses = computed(() => ({
  'bg-black/10 text-black dark:text-yapgray-200': props.style === TooltipStyle.Default,
  'bg-black/80 text-yapgray-200': props.style === TooltipStyle.Dark,
  'w-64': props.width === TooltipWidth.Small,
  'w-72': props.width === TooltipWidth.Medium,
  'w-80': props.width === TooltipWidth.Large
}));
</script>
